import {
  Activity,
  ArrowDownToLine,
  ArrowUpFromLine,
  Heart,
  LayoutDashboard,
  Lock,
  LogOut,
  Megaphone,
  MessageCircleQuestion,
  Package,
  Receipt,
  Repeat,
  ShieldCheck,
  UserCog,
  Users2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { SUBPAGES } from '@/pages/MemberApp';

const ICONS = {
  dashboard: LayoutDashboard,
  purchase: ArrowUpFromLine,
  withdraw: ArrowDownToLine,
  subpack_vault: Package,
  live_network: Activity,
  advertise: Megaphone,
  referrals: Users2,
  // Patch v17.5 — transfers nav item removed (see MemberApp.jsx SUBPAGES).
  global_pool: Heart,
  promo: Megaphone,
  ledger: Receipt,
  proof_of_work: ShieldCheck,
  cycle_proof_of_work: Repeat,
  support: MessageCircleQuestion,
  profile: UserCog,
};

const Sidebar = ({ active, onSelect, me }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <aside
      className="hidden lg:flex fixed left-0 top-0 bottom-0 w-[280px] z-40 flex-col bg-[rgba(8,8,9,0.78)] backdrop-blur-xl border-r border-[rgba(255,182,18,0.12)]"
      data-testid="sidebar"
    >
      {/* Brand */}
      <div className="px-6 pt-6 pb-5 border-b border-[rgba(255,182,18,0.10)]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#FFC20E] to-[#FFB612] shadow-[0_0_20px_rgba(255,182,18,0.35)]">
            <span className="text-[#0a0a0b] font-black font-display text-sm">1S</span>
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-[15px]">
              1SimpleCycler <span className="sc-gold-text">3.0</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Member Workspace</div>
          </div>
        </div>
      </div>

      {/* User card */}
      <div className="px-4 py-4 border-b border-[rgba(255,182,18,0.08)]">
        <div className="sc-glass rounded-xl p-3 flex items-center gap-3" data-testid="sidebar-user-card">
          <div
            className="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center font-display font-bold text-sm"
            style={{
              background: 'linear-gradient(180deg, rgba(255,182,18,0.18), rgba(255,182,18,0.02))',
              border: '1px solid rgba(255, 182, 18, 0.32)',
              color: '#FFE08A',
            }}
          >
            {me.username.slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm truncate">{me.username}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--sc-text-mute)] truncate">{me.referral_code}</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <div className="space-y-1">
          {SUBPAGES.map((s) => {
            const Icon = ICONS[s.id];
            const isActive = active === s.id;
            // Purchase/Deposit is gated on completing Step 1 of Getting
            // Started (sponsor verification) — prevents users depositing
            // with the wrong sponsor on file.
            const locked = s.id === 'purchase' && me && !me.sponsor_verified;
            const handleClick = () => {
              if (locked) {
                toast.error('Complete Step 1: Verify Your Sponsor first.', { description: 'Open the Dashboard and confirm your sponsor before depositing.' });
                return;
              }
              onSelect(s.id);
            };
            return (
              <button
                key={s.id}
                type="button"
                onClick={handleClick}
                aria-disabled={locked ? 'true' : 'false'}
                title={locked ? 'Locked — complete Step 1 first' : undefined}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-[14px] transition ${
                  isActive
                    ? 'bg-[rgba(255,182,18,0.10)] border border-[rgba(255,182,18,0.32)] text-[#FFC20E] font-semibold shadow-[inset_0_0_0_1px_rgba(255,182,18,0.08)]'
                    : locked
                      ? 'text-[var(--sc-text-mute)] border border-transparent opacity-55 cursor-not-allowed'
                      : 'text-[var(--sc-text-dim)] hover:text-[#FFC20E] hover:bg-[rgba(255,182,18,0.04)] border border-transparent'
                }`}
                data-testid={`nav-${s.id}`}
                data-locked={locked ? 'true' : 'false'}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="truncate flex-1">{s.label}</span>
                {locked ? <Lock className="w-3.5 h-3.5 shrink-0 text-[var(--sc-text-mute)]" /> : null}
              </button>
            );
          })}
        </div>
      </nav>

      <div className="px-3 pb-4 pt-2 border-t border-[rgba(255,182,18,0.08)] space-y-1">
        {me?.is_admin ? (
          <button
            type="button"
            onClick={() => navigate('/admin', { replace: false })}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-semibold text-[#FFC20E] bg-[rgba(255,182,18,0.06)] border border-[rgba(255,182,18,0.28)] hover:bg-[rgba(255,182,18,0.10)] transition"
            data-testid="nav-admin-console"
          >
            <ShieldCheck className="w-4 h-4" />
            Open Admin Console
          </button>
        ) : null}
        <button
          type="button"
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] text-[var(--sc-text-mute)] hover:text-[#FFC20E] hover:bg-[rgba(255,182,18,0.04)] transition"
          data-testid="nav-logout"
        >
          <LogOut className="w-4 h-4" />
          Switch / Sign out
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
